import { Check, Loader2, MailPlus, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useRevalidator } from "react-router-dom";
import { toast } from "sonner";

import { EmptyState, ErrorBanner } from "@/components/empty-state";
import type { TeamSwitcherTeam } from "@/components/team-switcher";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  type ActionResult,
  addMemberAction,
  removeMemberAction,
  switchTeamAction,
  updateMemberRoleAction,
} from "@/features/teams/actions";
import { CreateTeamDialog } from "@/features/teams/create-team-dialog";
import { api } from "@/lib/api";
import { useT } from "@/lib/i18n";
import {
  TEAM_ROLES,
  type TeamMember,
  type TeamMembersResponse,
  type TeamRole,
  type UserSummary,
} from "@/lib/types";
import { cn } from "@/lib/utils";
import { useAuthState } from "@/src/lib/auth";
import { invalidateAuthCaches, primeActiveTeamCaches } from "@/src/lib/loaders";

interface Props {
  me: UserSummary;
  activeTeamId: string;
  activeTeamName: string;
  isAdmin: boolean;
  /** Pre-fetched in the RSC shell so the page paints with content. */
  initialTeams: TeamSwitcherTeam[];
  initialMembers: TeamMember[] | null;
  /** When `initialMembers` failed to load, we surface the reason inline. */
  initialMembersError: string | null;
}

/**
 * `/teams` content. Two stacked cards:
 *
 *   1. "My teams"      — every team you're a member of. Click "Switch"
 *      to call `switchTeamAction`, which rotates your kos_token and
 *      revalidates the layout (so the active row hot-swaps).
 *   2. "Members of X"  — the same admin surface as the Settings team
 *      tab. Reused via the same server actions, just with a richer
 *      page-header chrome.
 *
 * Mirrors the shape of lemon_agent's `/users` route while reusing
 * everything we already shipped for the settings flow.
 */
export function TeamsBrowser({
  me,
  activeTeamId,
  activeTeamName,
  isAdmin,
  initialTeams,
  initialMembers,
  initialMembersError,
}: Props) {
  const t = useT();
  const revalidator = useRevalidator();
  const auth = useAuthState();
  // No `setTeams` — when the user switches or creates a team we revalidate
  // every active loader and the dashboard shell re-supplies the seed.
  const teams = initialTeams;
  const [createOpen, setCreateOpen] = useState(false);
  const [members, setMembers] = useState<TeamMember[] | null>(initialMembers);
  const [membersError, setMembersError] = useState<string | null>(initialMembersError);
  const adminCount = members?.filter((m) => m.role === "team_admin").length ?? 0;

  // Re-fetch members when the active team changes (e.g. after switching).
  // The seed comes from the RSC shell on first paint, so this is a
  // no-op on initial load.
  useEffect(() => {
    void activeTeamId;
    let cancelled = false;
    api<TeamMembersResponse>("/teams/current/members")
      .then((res) => {
        if (!cancelled) {
          setMembers(res.items);
          setMembersError(null);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setMembers([]);
        setMembersError(err instanceof Error ? err.message : "Failed to load members");
      });
    return () => {
      cancelled = true;
    };
  }, [activeTeamId]);

  const refreshMembers = async (): Promise<void> => {
    try {
      const res = await api<TeamMembersResponse>("/teams/current/members");
      setMembers(res.items);
      setMembersError(null);
    } catch (err) {
      setMembersError(err instanceof Error ? err.message : "Failed to refresh members");
    }
  };

  const onCreated = (result: Extract<ActionResult, { ok: true }>): void => {
    setCreateOpen(false);
    // The created team rotates the kos_token + sets it active. Prime
    // the dashboard cache first so the active workspace flips
    // immediately, then let revalidation reconcile with the server.
    const tenant = result.rotation?.tenant;
    if (tenant) {
      primeActiveTeamCaches(auth, teams, {
        team_id: tenant.team_id,
        team_slug: tenant.team_slug,
        team_name: tenant.team_name,
        org_id: tenant.org_id,
        org_name: tenant.org_name,
        org_plan: tenant.org_plan ?? auth.tenant.org_plan,
        role: tenant.role,
        is_active: true,
      });
    } else {
      invalidateAuthCaches();
    }
    revalidator.revalidate();
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader className="flex flex-row items-start justify-between space-y-0">
          <div className="space-y-1">
            <CardTitle className="text-base">{t("teams.section.mine")}</CardTitle>
            <CardDescription>{t("teams.section.mine.desc")}</CardDescription>
          </div>
          <Button
            type="button"
            variant="default"
            size="sm"
            onClick={() => setCreateOpen(true)}
            className="h-7 px-2 text-[11px]"
          >
            <Plus className="mr-1 h-3 w-3" />
            {t("teams.create")}
          </Button>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {teams.map((team) => (
              <TeamRow key={team.team_id} team={team} allTeams={teams} />
            ))}
          </ul>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("teams.section.members", { team: activeTeamName })}
          </CardTitle>
          <CardDescription>{t("teams.section.members.desc")}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {isAdmin ? <AddMemberForm onAdded={refreshMembers} /> : null}
          {membersError ? <ErrorBanner>{membersError}</ErrorBanner> : null}
          {members === null ? (
            <div className="space-y-2">
              <Skeleton className="h-8" />
              <Skeleton className="h-8" />
              <Skeleton className="h-8" />
            </div>
          ) : members.length === 0 ? (
            <EmptyState>{t("common.empty")}</EmptyState>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("teams.col.email")}</TableHead>
                  <TableHead>{t("teams.col.name")}</TableHead>
                  <TableHead>{t("teams.col.role")}</TableHead>
                  <TableHead>{t("teams.col.joined")}</TableHead>
                  {isAdmin || members.some((m) => m.user_id === me.id) ? (
                    <TableHead className="text-right" />
                  ) : null}
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => (
                  <MemberRow
                    key={member.user_id}
                    member={member}
                    isAdmin={isAdmin}
                    me={me}
                    adminCount={adminCount}
                    onChanged={refreshMembers}
                  />
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <CreateTeamDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={onCreated} />
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
// TeamRow — one row per team in the "My teams" card.
// ────────────────────────────────────────────────────────────────────

function TeamRow({ team, allTeams }: { team: TeamSwitcherTeam; allTeams: TeamSwitcherTeam[] }) {
  const t = useT();
  const [pending, setPending] = useState(false);
  const revalidator = useRevalidator();
  const auth = useAuthState();

  const onSwitch = async (): Promise<void> => {
    if (team.is_active) return;
    setPending(true);
    try {
      const result = await switchTeamAction(team.team_id);
      if (result.ok) {
        toast.success(result.message);
        // Re-run every loader so each route reinitialises against the
        // new active team. Prime the cache first so the visible
        // workspace flips immediately, then background refresh verifies
        // the server state.
        primeActiveTeamCaches(auth, allTeams, { ...team, is_active: true });
        revalidator.revalidate();
      } else {
        toast.error(result.message);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <li
      className={cn(
        "flex items-center gap-3 rounded-md border border-border/70 bg-card/40 p-3",
        team.is_active && "border-primary/30 bg-primary/5",
      )}
    >
      <span
        className={cn(
          "inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[12px] font-semibold uppercase",
          team.is_active ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground",
        )}
      >
        {team.team_name.slice(0, 2)}
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="truncate text-[13px] font-semibold text-foreground">
            {team.team_name}
          </span>
          {team.is_active ? (
            <Badge variant="lemon" className="h-4 px-1.5 text-[9px] uppercase tracking-[0.1em]">
              <Check className="mr-1 h-2.5 w-2.5" />
              {t("teams.active")}
            </Badge>
          ) : null}
          <Badge variant="outline" className="h-4 px-1.5 text-[9px] uppercase tracking-[0.1em]">
            {humanizeRole(t, team.role)}
          </Badge>
        </div>
        <p className="truncate text-[11px] text-muted-foreground">
          {team.org_name} · {team.team_slug}
        </p>
      </div>
      {team.is_active ? null : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void onSwitch()}
          disabled={pending}
          className="h-7 shrink-0 px-2 text-[11px]"
        >
          {pending ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
          {t("teams.switchTo")}
        </Button>
      )}
    </li>
  );
}

// ────────────────────────────────────────────────────────────────────
// AddMemberForm + MemberRow — same as settings tab, kept inline so we
// don't ripple a refactor of the settings tab into this PR. We can
// extract a shared `<MembersTable />` once both surfaces stabilize.
// ────────────────────────────────────────────────────────────────────

function AddMemberForm({ onAdded }: { onAdded: () => Promise<void> | void }) {
  const t = useT();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<TeamRole>("team_editor");
  const [pending, setPending] = useState(false);

  const submit = async (): Promise<void> => {
    setPending(true);
    try {
      const result = await addMemberAction(email, role);
      if (result.ok) {
        toast.success(result.message);
        setEmail("");
        await onAdded();
      } else {
        toast.error(result.message);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      className="flex flex-wrap items-end gap-2"
    >
      <div className="flex-1 space-y-1.5 sm:min-w-[260px]">
        <Label htmlFor="invite-email">{t("teams.col.email")}</Label>
        <Input
          id="invite-email"
          type="email"
          placeholder="teammate@company.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={pending}
        />
      </div>
      <div className="w-[140px] space-y-1.5">
        <Label htmlFor="invite-role">{t("teams.col.role")}</Label>
        <Select value={role} onValueChange={(v) => setRole(v as TeamRole)} disabled={pending}>
          <SelectTrigger id="invite-role">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {TEAM_ROLES.map((r) => (
              <SelectItem key={r} value={r}>
                {humanizeRole(t, r)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <Button type="submit" disabled={pending || !email.trim()}>
        {pending ? (
          <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
        ) : (
          <MailPlus className="mr-1.5 h-3.5 w-3.5" />
        )}
        {t("teams.addMember")}
      </Button>
    </form>
  );
}

function MemberRow({
  member,
  isAdmin,
  me,
  adminCount,
  onChanged,
}: {
  member: TeamMember;
  isAdmin: boolean;
  me: UserSummary;
  adminCount: number;
  onChanged: () => Promise<void> | void;
}) {
  const t = useT();
  const [pending, setPending] = useState(false);
  const isMe = member.user_id === me.id;
  const isLastAdmin = member.role === "team_admin" && adminCount <= 1;
  const canRemove = (isAdmin || isMe) && !isLastAdmin;
  const canChangeRole = isAdmin && !isLastAdmin;

  const onRoleChange = async (next: string): Promise<void> => {
    if (next === member.role) return;
    setPending(true);
    try {
      const result = await updateMemberRoleAction(member.user_id, next);
      if (result.ok) {
        toast.success(result.message);
        await onChanged();
      } else {
        toast.error(result.message);
      }
    } finally {
      setPending(false);
    }
  };

  const onRemove = async (): Promise<void> => {
    const label = isMe ? "Leave this team?" : `Remove ${member.email}?`;
    if (!window.confirm(label)) return;
    setPending(true);
    try {
      const result = await removeMemberAction(member.user_id);
      if (result.ok) {
        toast.success(result.message);
        await onChanged();
      } else {
        toast.error(result.message);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <TableRow>
      <TableCell className="font-mono text-xs">
        {member.email}
        {isMe ? <span className="ml-1 text-muted-foreground">(you)</span> : null}
      </TableCell>
      <TableCell>{member.name ?? "—"}</TableCell>
      <TableCell>
        {canChangeRole ? (
          <Select
            value={member.role}
            onValueChange={(v) => void onRoleChange(v)}
            disabled={pending}
          >
            <SelectTrigger className="h-7 w-[110px] text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TEAM_ROLES.map((r) => (
                <SelectItem key={r} value={r}>
                  {humanizeRole(t, r)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        ) : (
          <Badge variant="secondary">{humanizeRole(t, member.role)}</Badge>
        )}
      </TableCell>
      <TableCell className="text-xs text-muted-foreground">
        {new Date(member.joined_at).toLocaleDateString()}
      </TableCell>
      {isAdmin || isMe ? (
        <TableCell className="text-right">
          {canRemove ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => void onRemove()}
              disabled={pending}
              className="h-7 px-2 text-destructive hover:text-destructive"
            >
              <Trash2 className="mr-1 h-3.5 w-3.5" />
              {isMe ? "Leave" : "Remove"}
            </Button>
          ) : isLastAdmin ? (
            <span className="text-xs text-muted-foreground">last admin</span>
          ) : null}
        </TableCell>
      ) : null}
    </TableRow>
  );
}

function humanizeRole(t: ReturnType<typeof useT>, role: string): string {
  if (role === "team_admin") return t("teams.role.admin");
  if (role === "team_editor") return t("teams.role.editor");
  if (role === "team_viewer") return t("teams.role.viewer");
  return role;
}
